/**
 * Where the reader last was, so the app has somewhere to start.
 *
 * Geolocation is refused, unavailable or slow often enough that
 * "nearest first" otherwise degrades to alphabetical — which puts a
 * site in Egypt above one an hour's drive away. Remembering the last
 * fix, or the last place they panned the map to, makes the next visit
 * useful without asking again.
 *
 * Two sources, deliberately ranked:
 *   "device" — an actual geolocation fix. Trusted most.
 *   "map"    — the middle of a map view they chose. Weaker evidence of
 *              where they are, but strong evidence of where they care
 *              about, which is what the ordering is really for.
 *
 * A device fix always displaces a remembered map centre; a map centre
 * only displaces another map centre, so panning to look at Palau does
 * not erase a real fix taken this morning.
 */

import { coordinateInRange, parseCoordinate } from "./utils.js";

const KEY = "divecohorts_last_location";
/** Old enough that "near you" would be a lie. A month still beats
 *  alphabetical, and a diver's home water rarely moves. */
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

/** A longitude folded into [-180, 180). -185 and 175 are the same
 *  meridian; only one of them is storable. */
function wrapLongitude(lng) {
  if (!Number.isFinite(lng)) return lng;
  return (((lng + 180) % 360) + 360) % 360 - 180;
}

function valid(lat, lng) {
  return (
    coordinateInRange(lat, 90) && coordinateInRange(lng, 180) &&
    // 0,0 is in the Gulf of Guinea and is almost always a coordinate
    // that failed to arrive rather than one anybody chose.
    !(Number(lat) === 0 && Number(lng) === 0)
  );
}

/** The remembered place, or null. */
export function lastKnownLocation() {
  let stored;
  try {
    stored = JSON.parse(localStorage.getItem(KEY) || "null");
  } catch (_) {
    // Hand-edited or written by an older version. Not worth reporting.
    return null;
  }
  if (!stored || !valid(stored.lat, stored.lng)) return null;
  if (!Number.isFinite(stored.at) || Date.now() - stored.at > MAX_AGE_MS) return null;
  return {
    lat: parseCoordinate(stored.lat),
    lng: parseCoordinate(stored.lng),
    zoom: Number.isFinite(stored.zoom) ? stored.zoom : null,
    source: stored.source === "device" ? "device" : "map",
    at: stored.at,
  };
}

/**
 * Records a place. Returns true if it was kept.
 *
 * Quietly ignores a map centre when a device fix is already remembered
 * and still fresh — see the ranking above.
 */
export function rememberLocation({ lat, lng, zoom = null, source = "map" } = {}) {
  // Wrapped before validating. Pan a map west past 180° and Leaflet
  // reports a centre like -185: the same place as 175, but outside the
  // range the check allows — so the view was silently not remembered
  // once the reader crossed the dateline.
  const longitude = wrapLongitude(parseCoordinate(lng));
  const latitude = parseCoordinate(lat);
  if (!valid(latitude, longitude)) return false;

  if (source !== "device") {
    const existing = lastKnownLocation();
    if (existing && existing.source === "device") return false;
  }

  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        lat: latitude,
        lng: longitude,
        zoom: Number.isFinite(Number(zoom)) ? Number(zoom) : null,
        source: source === "device" ? "device" : "map",
        at: Date.now(),
      })
    );
    return true;
  } catch (_) {
    // Private browsing, or a full quota. The app works without this.
    return false;
  }
}

export function forgetLocation() {
  try {
    localStorage.removeItem(KEY);
  } catch (_) {
    /* nothing to do */
  }
}
